import React,{useState,useMemo}from"react";
import{X,Search,User,Users,Layers}from"lucide-react";
import{BLUE,dispName}from"../config";
import{StaBadge}from"./UI";

export default function DepsPicker({all,cu,selected,onChange,onClose,excludeId,projects=[]}){
  const[search,setSearch]=useState("");
  const[showCompleted,setShowCompleted]=useState(false);
  const[filterProject,setFilterProject]=useState("");
  const myEmail=(cu.email||"").toLowerCase();

  // Projects that have at least one task in `all`
  const projectOptions=useMemo(()=>{
    const ids=new Set(all.filter(t=>t.project).map(t=>t.project));
    return projects.filter(p=>ids.has(p.spId));
  },[all,projects]);

  const filtered=useMemo(()=>{
    const q=search.toLowerCase();
    return all.filter(t=>{
      if(excludeId&&t.id===excludeId)return false;
      if(!showCompleted&&t.sta==="completed")return false;
      if(filterProject&&t.project!==filterProject)return false;
      if(!q)return true;
      return t.title.toLowerCase().includes(q)||dispName(t.own).toLowerCase().includes(q);
    });
  },[all,search,showCompleted,excludeId,filterProject]);

  const myTasks=filtered.filter(t=>t.listType!=="company"&&(t.own.toLowerCase()===myEmail||t.asgn.toLowerCase()===myEmail));
  const companyTasks=filtered.filter(t=>t.listType==="company"||t.vis==="company");

  const toggle=id=>onChange(selected.includes(id)?selected.filter(x=>x!==id):[...selected,id]);

  function TaskItem({t}){
    const sel=selected.includes(t.id);
    const proj=t.project?projects.find(p=>p.spId===t.project):null;
    return(
      <label className={"flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer border "+(sel?"bg-blue-50 border-blue-200":"border-transparent hover:bg-slate-50")}>
        <input type="checkbox" checked={sel} onChange={()=>toggle(t.id)} className="shrink-0 accent-blue-600"/>
        <span className="flex-1 min-w-0">
          <span className={"text-sm block truncate "+(t.sta==="completed"?"line-through text-slate-400":"")}>{t.title}</span>
          {proj&&<span className="text-xs text-indigo-500 flex items-center gap-1 mt-0.5"><Layers size={9}/>{proj.jobNumber?proj.jobNumber+" – "+proj.projectName:proj.projectName}</span>}
        </span>
        {t.own.toLowerCase()!==myEmail&&<span className="text-xs text-slate-400 shrink-0">{dispName(t.own)}</span>}
        <StaBadge sta={t.sta}/>
      </label>
    );
  }

  const empty=myTasks.length===0&&companyTasks.length===0;

  return(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.55)",zIndex:60}}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col" style={{maxHeight:"78vh"}}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div>
            <p className="font-bold text-slate-800">Select Dependencies</p>
            <p className="text-xs text-slate-400 mt-0.5">Tasks that must complete before this one · {selected.length} selected</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={17}/></button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 shrink-0 space-y-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tasks…" className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-blue-300"/>
          </div>
          {projectOptions.length>0&&(
            <div className="flex items-center gap-2">
              <Layers size={13} className="text-slate-400 shrink-0"/>
              <select value={filterProject} onChange={e=>setFilterProject(e.target.value)} className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
                <option value="">All Projects</option>
                {projectOptions.map(p=>(
                  <option key={p.spId} value={p.spId}>{p.jobNumber?p.jobNumber+" – "+p.projectName:p.projectName}{p.customerName?" ("+p.customerName+")":""}</option>
                ))}
              </select>
              {filterProject&&<button onClick={()=>setFilterProject("")} className="text-xs text-slate-400 hover:text-slate-600 shrink-0"><X size={12}/></button>}
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer select-none">
            <input type="checkbox" checked={showCompleted} onChange={e=>setShowCompleted(e.target.checked)} className="accent-blue-600"/>
            Include completed tasks
          </label>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {myTasks.length>0&&(
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1"><User size={10}/>My Tasks</p>
              <div className="space-y-0.5">{myTasks.map(t=><TaskItem key={t.id} t={t}/>)}</div>
            </div>
          )}
          {companyTasks.length>0&&(
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1"><Users size={10}/>Company Tasks</p>
              <div className="space-y-0.5">{companyTasks.map(t=><TaskItem key={t.id} t={t}/>)}</div>
            </div>
          )}
          {empty&&<p className="text-sm text-slate-400 text-center py-8">{search||filterProject?"No tasks match the current filters.":"No tasks available."}</p>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 shrink-0 flex items-center justify-between">
          <button onClick={()=>onChange([])} className="text-xs text-slate-400 hover:text-slate-600 disabled:opacity-40" disabled={selected.length===0}>Clear all</button>
          <button onClick={onClose} className="px-5 py-2 text-sm text-white font-semibold rounded-lg" style={{background:BLUE}}>Done{selected.length>0?` (${selected.length})`:""}</button>
        </div>
      </div>
    </div>
  );
}
